import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { shortDurationString, longDateString } from "../../utils";
import "../../starek.css";

function CyclesListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    noDataComponent,
    onRowClicked,
}) {
    const isCycleSelected = (cycleId) => {
        return selectedRows.some((row) => row.id === cycleId);
    };

    const handleCheckboxChange = (cycle) => {
        let newSelectedRows;
        if (isCycleSelected(cycle.id)) {
            newSelectedRows = selectedRows.filter((row) => row.id !== cycle.id);
        } else {
            newSelectedRows = [...selectedRows, cycle];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [items, filterText]);

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all"
                    onChange={(e) => {
                        if (e.target.checked) {
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({
                                    selectedRows: [...filteredItems],
                                });
                            }
                        } else {
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({ selectedRows: [] });
                            }
                        }
                    }}
                    checked={
                        filteredItems.length > 0 &&
                        filteredItems.every((row) => isCycleSelected(row.id))
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = filteredItems.some((r) =>
                            isCycleSelected(r.id)
                        );
                        const all =
                            filteredItems.length > 0 &&
                            filteredItems.every((r) => isCycleSelected(r.id));
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={isCycleSelected(row.id)}
                    onChange={() => handleCheckboxChange(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Stages",
            cell: (row) =>
                row.cycle_stages ? `${row.cycle_stages.length} stage(s)` : "0",
            sortable: false,
        },
        {
            name: "Total Duration",
            cell: (row) => {
                const totalDuration =
                    row.cycle_stages?.reduce(
                        (acc, stage) => acc + stage.duration,
                        0
                    ) || 0;
                return shortDurationString(totalDuration);
            },
            sortable: false,
            grow: 1,
        },
        {
            name: "Activity",
            selector: (row) => row.updated_at,
            sortable: true,
            width: "250px",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                            <div>
                                Created by {row.created_by?.name || "Unknown"}{" "}
                                at {longDateString(row.created_at)}
                            </div>
                            <div>
                                Modified by {row.updated_by?.name || "Unknown"}{" "}
                                at {longDateString(row.updated_at)}
                            </div>
                        </Tooltip>
                    }
                >
                    <div style={{ fontSize: "0.85rem" }}>
                        {row.updated_by?.name || "Unknown"} at{" "}
                        {longDateString(row.updated_at)}
                    </div>
                </OverlayTrigger>
            ),
        },
        {
            name: "Status",
            width: "90px",
            cell: (r) => (
                <span className={`badge bg-${r.is_active ? "success" : "secondary"}`}>
                    {r.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
    ];

    const conditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredItems}
            highlightOnHover
            pointerOnHover
            onRowClicked={(row) => onRowClicked?.(row)}
            striped
            responsive
            noDataComponent={noDataComponent}
            conditionalRowStyles={conditionalRowStyles}
        />
    );
}

export default CyclesListingView;
